import { Box, EmptyState } from '@dixa/lib-shared-habla';
import {
  getBackendOptions,
  MultiBackend,
  Tree,
} from '@minoru/react-dnd-treeview';
import {
  DragPreviewRender,
  NodeRender,
} from '@minoru/react-dnd-treeview/dist/types';
import { CollectionTreeQuery } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { DndProvider } from 'react-dnd';

import ArticleNode from './components/articleNode';
import CategoryNode from './components/categoryNode';
import { CategoryAddModal } from './components/categoryNode/modals/categoryAddModal';
import { Placeholder } from './components/placeholder/placeholder';
import { useCategoriesOpen } from './hooks/useCategoriesOpen';
import { NodeModelData, useDragAndDrop } from './hooks/useDragAndDrop';
import s from './styles.module.scss';

type Props = {
  collectionData?: CollectionTreeQuery;
};

export const CollectionTree = ({ collectionData }: Props): JSX.Element => {
  const { showModal } = useModal();
  const { setOpenCategoryIds, openCategoryIds } = useCategoriesOpen(
    collectionData?.collection?.id || '',
  );
  const {
    onDrop: handleDrop,
    canDrop,
    treeData,
  } = useDragAndDrop(collectionData);

  if (!collectionData || !collectionData.collection) return <></>;

  const hasCategories = collectionData.collection.categories?.length > 0;
  if (!hasCategories) {
    return (
      <div className={s.collectionWrapper}>
        <EmptyState
          className={s.noCategory}
          title="There is no category"
          description="You will need to make a category for it to show here."
          buttonProps={{
            children: 'New Category',
            onClick: () =>
              showModal(CategoryAddModal, { presetParent: undefined }),
          }}
        />
      </div>
    );
  }

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <Box className={s.collectionTreeWrapper}>
        <Tree
          onChangeOpen={setOpenCategoryIds}
          initialOpen={openCategoryIds}
          sort={false}
          tree={treeData}
          rootId={0}
          onDrop={handleDrop}
          classes={{
            container: s.collectionWrapper,
            draggingSource: s.draggingSource,
            dropTarget: s.dropTarget,
            placeholder: s.placeholderContainer,
          }}
          dropTargetOffset={10}
          enableAnimateExpand={true}
          placeholderRender={(node, { depth }) => (
            <Placeholder node={node} depth={depth} />
          )}
          dragPreviewRender={dragPreviewRender}
          canDrop={canDrop}
          render={renderNode}
        />
      </Box>
    </DndProvider>
  );
};

const renderNode: NodeRender<NodeModelData> = (
  node,
  { depth, isOpen, onToggle, isDragging, handleRef },
) => {
  if (!node.data) return <></>;

  return node.data?.node.__typename === 'Category' ? (
    <CategoryNode
      node={node.data.node}
      isDragging={isDragging}
      isOpen={isOpen}
      onToggleOpen={onToggle}
      handleRef={handleRef}
      depth={depth}
    />
  ) : (
    <ArticleNode
      node={node.data.node}
      isDragging={isDragging}
      handleRef={handleRef}
      depth={depth}
    />
  );
};

const dragPreviewRender: DragPreviewRender<NodeModelData> = (monitorProps) => {
  const node = monitorProps.item.data?.node;
  if (!node) return <></>;
  if (node.__typename === 'Category') {
    return (
      <CategoryNode
        node={node}
        isPreview={true}
        isOpen={false}
        depth={0}
        onToggleOpen={() => null}
      />
    );
  }
  return <ArticleNode node={node} isDragging={true} depth={0} />;
};

export default CollectionTree;
